import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../data/constants';
import { getLumberLocationByItem } from '../../api/ebs-mfg-api/index.js';
import { useApiReady } from '@pella/fetch-client-react';

/**
 * Hook to fetch the lumber location from ebs-mfg given a valid item number.
 * Returns a normalized shape: { data, isPending, error } where error is
 * a human-readable message string covering both API failures and soft
 * not-found cases (no item match on the response).
 * @param {object} input
 * @param {string | undefined} input.item
 * @param {boolean} [input.enabled] - Optional override; when false the query is disabled regardless of other params
 */
const useLumberPartLookup = ({ item, enabled: enabledOverride = true }) => {
  const { apiReady } = useApiReady();
  const query = useQuery({
    queryKey: [QUERY_KEYS.LUMBER_LOCATION_BY_ITEM, item],
    queryFn: () =>
      getLumberLocationByItem({
        itemNumber: /** @type {string} */ (item),
      }),
    enabled: Boolean(enabledOverride && apiReady && item),
  });

  const error =
    query.error?.message ||
    (query.isSuccess && !query.data?.item ? 'Invalid item. No location found for the entered item number.' : null) ||
    null;

  return {
    data: query.data,
    isPending: query.isPending,
    error,
  };
};

export { useLumberPartLookup };
